// src/hooks/usePWAInstall.js - Enhanced with UAParser for better device detection
import { useState, useEffect, useCallback, useRef } from 'react';
import { UAParser } from 'ua-parser-js';

export const usePWAInstall = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installError, setInstallError] = useState(null);
  const [platform, setPlatform] = useState('unknown');
  const [canInstall, setCanInstall] = useState(false);
  const [installMethod, setInstallMethod] = useState('prompt');
  const [browserSupport, setBrowserSupport] = useState('unknown');
  const [deviceInfo, setDeviceInfo] = useState({});
  
  const deferredPromptRef = useRef(null);
  const installCheckRef = useRef(null);
  const parser = useRef(new UAParser());

  // Advanced device and browser detection using UAParser
  useEffect(() => {
    const detectDevice = () => {
      const result = parser.current.getResult();
      
 
      const deviceData = {
        browser: result.browser,
        device: result.device,
        os: result.os,
        engine: result.engine,
        cpu: result.cpu
      };
      
      setDeviceInfo(deviceData);
      
      let detectedPlatform = 'desktop';
      let detectedMethod = 'prompt';
      let detectedSupport = 'full';
      let canInstallDevice = false;

      // Enhanced iOS Detection
      const isIOSDevice = 
        result.os.name === 'iOS' ||
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
        (result.device.type === 'mobile' && result.os.name === 'Mac OS') ||
        (result.device.vendor === 'Apple' && result.device.type === 'mobile');
      
      // Enhanced Safari Detection
      const isSafari = 
        result.browser.name === 'Safari' ||
        result.browser.name === 'Mobile Safari' ||
        (/Safari/.test(navigator.userAgent) && !/Chrome|CriOS|OPiOS|FxiOS|EdgiOS/.test(navigator.userAgent));
      
      const isChrome = result.browser.name === 'Chrome' || result.browser.name === 'Chrome Mobile';
      const isAndroid = result.os.name === 'Android';
      

      if (isIOSDevice) {
        detectedPlatform = 'ios';
        detectedMethod = 'manual';
        canInstallDevice = true; // Always allow installation attempt on iOS
        
        if (isSafari) {
          detectedSupport = 'safari';
       
        } else {
          detectedSupport = 'limited';
         
        }
      } else if (isAndroid) {
        detectedPlatform = 'android';
        canInstallDevice = true;
        
        if (isChrome) {
          const chromeVersion = parseInt(result.browser.version?.split('.')[0] || '0');
          if (chromeVersion >= 68) {
            detectedMethod = 'prompt';
            detectedSupport = 'full';
          } else {
            detectedMethod = 'manual';
            detectedSupport = 'partial';
          }
        } else {
          detectedMethod = 'manual';
          detectedSupport = 'partial';
        }
      } else if (result.device.type === 'mobile' || result.device.type === 'tablet') {
        detectedPlatform = 'mobile';
        detectedMethod = 'manual';
        detectedSupport = 'partial';
        canInstallDevice = true;
      } else {
        // Desktop - DISABLE PWA
        detectedPlatform = 'desktop';
        detectedMethod = 'none';
        detectedSupport = 'disabled';
        canInstallDevice = false; // ALWAYS FALSE for desktop
      }

      setPlatform(detectedPlatform);
      setInstallMethod(detectedMethod);
      setBrowserSupport(detectedSupport);
      setCanInstall(canInstallDevice);
      
      // For iOS, immediately set as installable
      if (isIOSDevice && !window.navigator.standalone) {
      
        setIsInstallable(true);
        
        // Trigger custom event for iOS PWA
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('pwa-install-available', {
            detail: { 
              prompt: null,
              platform: 'ios',
              method: 'manual',
              canInstall: true,
              browser: isSafari ? 'safari' : 'other',
              deviceInfo: deviceData
            }
          }));
        }, 100);
      }
      
      // DISABLE for desktop completely
      if (detectedPlatform === 'desktop') {
 
        setIsInstallable(false);
        setCanInstall(false);
      }
      
      return { 
        platform: detectedPlatform, 
        method: detectedMethod, 
        support: detectedSupport,
        canInstall: canInstallDevice,
        deviceData
      };
    };

    const platformInfo = detectDevice();
    
    // Enhanced installation status check
    const checkInstallationStatus = () => {
      try {
        // Never show as installable on desktop
        if (platformInfo.platform === 'desktop') {
          setIsInstalled(false);
          setIsInstallable(false);
          setCanInstall(false);
          return false;
        }
        
        // Check if already installed
        const isStandalone = 
          window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true ||
          document.referrer.includes('android-app://') ||
          window.matchMedia('(display-mode: minimal-ui)').matches ||
          window.matchMedia('(display-mode: fullscreen)').matches;

        // iOS specific check
        if (platformInfo.platform === 'ios') {
          const isIOSInstalled = 
            window.navigator.standalone === true ||
            (document.referrer === '' && window.history.length === 1 && isStandalone);
          
          setIsInstalled(isIOSInstalled);
          
          if (!isIOSInstalled) {
          
            setIsInstallable(true);
            setCanInstall(true);
          }
          
          return isIOSInstalled;
        }

        setIsInstalled(isStandalone);
        return isStandalone;
      } catch (error) {
     
        setIsInstalled(false);
        return false;
      }
    };

    const installationStatus = checkInstallationStatus();
    
    // Set up periodic checks for installation status (NOT for desktop)
    if (!installationStatus && platformInfo.canInstall && platformInfo.platform !== 'desktop') {
      installCheckRef.current = setInterval(checkInstallationStatus, 3000);
    }

    // Cleanup interval on unmount or when installed
    return () => {
      if (installCheckRef.current) {
        clearInterval(installCheckRef.current);
      }
    };
  }, []);

  // Enhanced beforeinstallprompt and custom event listener
  useEffect(() => {
    let mounted = true;

    // Skip everything for desktop
    if (platform === 'desktop') {
      
      return;
    }

    const handleInstallPrompt = (event) => {
      if (!mounted || platform === 'desktop') return;
      
      

      if (event.preventDefault) {
        event.preventDefault();
      }
      
      deferredPromptRef.current = event;
      setInstallPrompt(event);
      setIsInstallable(true);
      setCanInstall(true);
      window.deferredPrompt = event;
    };

    const handleCustomInstallAvailable = (event) => {
      if (!mounted || platform === 'desktop') return;
      
     
      
      const { prompt, platform: eventPlatform, method, canInstall: eventCanInstall } = event.detail;
      
      if (prompt) {
        deferredPromptRef.current = prompt;
        setInstallPrompt(prompt);
        window.deferredPrompt = prompt;
      }
      
      setIsInstallable(true);
      setCanInstall(eventCanInstall !== false);
      
      if (method) {
        setInstallMethod(method);
      }
    };

    const handleAppInstalled = () => {
      if (!mounted) return;
      
 
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
      setCanInstall(false);
      deferredPromptRef.current = null;
      window.deferredPrompt = null;
      
      if (installCheckRef.current) {
        clearInterval(installCheckRef.current);
      }

      window.dispatchEvent(new CustomEvent('pwa-installed', {
        detail: { platform, timestamp: Date.now() }
      }));
    };

    // Listen for install events
    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('pwa-install-available', handleCustomInstallAvailable);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if prompt is already available
    if (window.deferredPrompt && !isInstalled && platform !== 'desktop') {
      handleInstallPrompt(window.deferredPrompt);
    }

    return () => {
      mounted = false;
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('pwa-install-available', handleCustomInstallAvailable);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [platform, browserSupport, isInstalled]);

  // Enhanced installation function
  const install = useCallback(async () => {
    if (isInstalling || isInstalled || platform === 'desktop') {
      return false;
    }

    setIsInstalling(true);
    setInstallError(null);

    try {
      // iOS manual installation
      if (platform === 'ios') {
   
        setIsInstalling(false);
        return 'manual';
      }

      // Standard prompt-based installation
      const prompt = deferredPromptRef.current || installPrompt;
      
      if (!prompt) {
       
        setIsInstalling(false);
        return 'manual';
      }

     
      
      const result = await prompt.prompt();
      

      if (result.outcome === 'accepted') {
       
        
        setTimeout(() => {
          const checkInstalled = () => {
            const isNowInstalled = 
              window.matchMedia('(display-mode: standalone)').matches ||
              window.navigator.standalone === true ||
              document.referrer.includes('android-app://');
            
            if (isNowInstalled) {
              setIsInstalled(true);
              setIsInstallable(false);
              setCanInstall(false);
            }
          };
          
          checkInstalled();
          setTimeout(checkInstalled, 2000);
        }, 1000);

        setInstallPrompt(null);
        deferredPromptRef.current = null;
        window.deferredPrompt = null;
        
        return true;
      } else {
    
        return false;
      }
    } catch (error) {
   
      setInstallError(error);
      return 'manual';
    } finally {
      setIsInstalling(false);
    }
  }, [installPrompt, isInstalling, isInstalled, platform]);

  // Enhanced installation instructions
  const getInstallInstructions = useCallback(() => {
    const baseInstructions = {
      title: 'Install Wells',
      subtitle: 'Get the full app experience',
      benefits: [
        ''
      ]
    };

    switch (platform) {
      case 'ios':
        const isSafari = deviceInfo.browser?.name === 'Safari' || 
                        deviceInfo.browser?.name === 'Mobile Safari' ||
                        (/Safari/.test(navigator.userAgent) && !/Chrome|CriOS|OPiOS|FxiOS|EdgiOS/.test(navigator.userAgent));
        
        if (isSafari) {
          return {
            ...baseInstructions,
            method: 'safari',
            steps: [
              'Tap the Share button at the bottom of Safari',
              'Scroll down and tap "Add to Home Screen"',
              'Tap "Add" in the top right corner',
              'The Wells app will appear on your home screen'
            ],
            icons: ['📱', '⬆️', '🏠', '✅'],
            requirements: 'iOS Safari browser required'
          };
        } else {
          return {
            ...baseInstructions,
            method: 'ios-other',
            steps: [
              'Copy this page URL and open it in Safari',
              'Tap the Share button at the bottom of Safari',
              'Scroll down and tap "Add to Home Screen"',
              'Tap "Add" to complete installation'
            ],
            icons: ['📋', '🌐', '🏠', '✅'],
            requirements: 'Safari browser required for iOS installation'
          };
        }

      case 'android':
        if (browserSupport === 'full') {
          return {
            ...baseInstructions,
            method: 'prompt',
            steps: [
              'Tap "Add to Home Screen" in the popup',
              'Or tap the menu (⋮) and select "Add to Home Screen"',
              'Confirm by tapping "Add" or "Install"',
              'The app will appear on your home screen'
            ],
            icons: ['📱', '⋮', '➕', '✅'],
            requirements: `Chrome ${deviceInfo.browser?.version || '68'}+ on Android`
          };
        } else {
          return {
            ...baseInstructions,
            method: 'manual',
            steps: [
              'Tap the menu (⋮) in your browser',
              'Look for "Add to Home Screen" or "Install App"',
              'Tap it and confirm the installation',
              'The app will appear on your home screen'
            ],
            icons: ['⋮', '🔍', '➕', '✅'],
            requirements: 'Android 5.0+ with supported browser'
          };
        }

      default:
        return {
          ...baseInstructions,
          method: 'not-available',
          steps: [
            'PWA installation is not available on desktop'
          ],
          icons: ['🚫'],
          requirements: 'Use a mobile device to install Wells'
        };
    }
  }, [platform, browserSupport, deviceInfo]);

  // Enhanced browser support checking
  const getBrowserSupportInfo = useCallback(() => {
    const supportInfo = {
      isSupported: false,
      level: 'none',
      message: '',
      recommendation: ''
    };

    // Desktop is NOT supported
    if (platform === 'desktop') {
      supportInfo.isSupported = false;
      supportInfo.level = 'disabled';
      supportInfo.message = 'Desktop installation disabled';
      supportInfo.recommendation = 'Use a mobile device to install Wells';
      return supportInfo;
    }

    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasManifest = 'onbeforeinstallprompt' in window;

    if (platform === 'ios') {
      const isSafari = deviceInfo.browser?.name === 'Safari' || 
                      deviceInfo.browser?.name === 'Mobile Safari' ||
                      (/Safari/.test(navigator.userAgent) && !/Chrome|CriOS|OPiOS|FxiOS|EdgiOS/.test(navigator.userAgent));
      
      supportInfo.isSupported = true; // Always supported on iOS
      supportInfo.level = isSafari ? 'manual' : 'limited';
      supportInfo.message = isSafari ? 'Manual installation available' : 'Limited support in this browser';
      supportInfo.recommendation = isSafari ? 'Tap install guide to continue' : 'Please open in Safari for installation';
    } else if (platform === 'android') {
      const isChrome = deviceInfo.browser?.name === 'Chrome' || deviceInfo.browser?.name === 'Chrome Mobile';
      const chromeVersion = parseInt(deviceInfo.browser?.version?.split('.')[0] || '0');
      
      if (isChrome && chromeVersion >= 68) {
        supportInfo.isSupported = true;
        supportInfo.level = 'full';
        supportInfo.message = 'Full PWA support available';
        supportInfo.recommendation = 'Install for best experience';
      } else if (hasServiceWorker && hasManifest) {
        supportInfo.isSupported = true;
        supportInfo.level = 'partial';
        supportInfo.message = 'Basic installation available';
        supportInfo.recommendation = 'Consider using Chrome for better support';
      } else {
        supportInfo.isSupported = false;
        supportInfo.level = 'limited';
        supportInfo.message = 'Limited PWA support';
        supportInfo.recommendation = 'Update browser or use Chrome';
      }
    }

    return supportInfo;
  }, [platform, deviceInfo]);

  // Debug logging for iOS
  useEffect(() => {
    if (platform === 'ios') {
      
    }
  }, [platform, isInstallable, isInstalled, canInstall, browserSupport, installMethod, deviceInfo]);

  return {
    // State
    isInstallable: platform !== 'desktop' && isInstallable && !isInstalled,
    isInstalled,
    isInstalling,
    installError,
    platform,
    installMethod,
    browserSupport,
    canInstall: platform !== 'desktop' && canInstall && !isInstalled,
    
    // Functions
    install,
    getInstallInstructions,
    getBrowserSupportInfo,
    
    // Enhanced info
    browserInfo: {
      name: deviceInfo.browser?.name || 'Unknown',
      version: deviceInfo.browser?.version || 'Unknown',
      userAgent: navigator.userAgent
    },
    deviceInfo: {
      os: deviceInfo.os?.name || 'Unknown',
      osVersion: deviceInfo.os?.version || 'Unknown',
      platform,
      type: deviceInfo.device?.type || 'desktop',
      vendor: deviceInfo.device?.vendor || 'Unknown',
      model: deviceInfo.device?.model || 'Unknown'
    }
  };
};